// components/DepartmentDialog.tsx
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
} from "@/hooks/api/department.queries";
import {
  DepartmentCreateType,
  DepartmentParamsType,
  DepartmentType,
} from "@/services/department.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import TextareaField from "../form/components/Textarea";


type DepartmentDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: DepartmentParamsType;
  deptId?: string;
  deptData?: DepartmentType | null;
  onCancel: () => void;
  /** Called with the new department ID after successful creation */
  onSuccess?: (newDeptId: string) => void;
};

const departmentSchema = z.object({
  name: z.string().min(1, "Department Name is required"),
  code: z.string().min(1, "Department Code is required"),
  email: z.email("Invalid email address"),
  phone: z.string().optional(),
  established_year: z.coerce
    .number()
    .min(1800, "Invalid year")
    .max(new Date().getFullYear(), "Year cannot be in the future")
    .optional(),
  // building: z.string().optional(),
  // floor: z.string().optional(),
  status: z.enum(["active", "inactive"]),
  description: z.string().optional(),
});

type DepartmentFormType = z.infer<typeof departmentSchema>;

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const DepartmentDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  deptId,
  deptData,
  onCancel,
  onSuccess,
}: DepartmentDialogProps) => {
  const navigate = useNavigate();
  const createMutation = useCreateDepartmentMutation(filters);
  const updateMutation = useUpdateDepartmentMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<DepartmentFormType>({
    resolver: zodResolver(departmentSchema as any),
    defaultValues: {
      name: "",
      code: "",
      email: "",
      phone: "",
      established_year: new Date().getFullYear(),
      status: "active",
      description: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && deptData) {
        setTimeout(() => {
          form.reset({
            name: deptData.name,
            code: deptData.code,
            email: deptData.email,
            phone: deptData.phone,
            established_year: deptData.established_year,
            // building: deptData.building,
            // floor: deptData.floor,
            status: deptData.is_active ? "active" : "inactive",
            description: deptData.description,
          });
        }, 0);
      } else if (mode === "create") {
        if (!deptData) {
          // Only reset to empty if we don't have deptData (true initial open)
          form.reset({
            name: "",
            code: "",
            email: "",
            phone: "",
            established_year: new Date().getFullYear(),
            status: "active",
            description: "",
          });
        }
      }
    }
  }, [mode, deptData, isOpen, form]);

  const handleSubmit = (values: DepartmentFormType) => {
    // Ensure numbers are numbers and handle optionals
    const submissionData: DepartmentCreateType = {
      code: values.code,
      name: values.name,
      email: values.email,
      status: values.status,
      phone: values.phone || "",
      description: values.description || "",
      established_year: values.established_year || new Date().getFullYear(),
    };

    const INLINE_DRAFT_KEY = "inline_create_draft";

    if (mode === "create") {
      createMutation.mutate(submissionData, {
        onSuccess: (res: any) => {
          const newDeptId = res?.data?.id ?? res?.id;
          if (newDeptId) onSuccess?.(newDeptId);
          onCancel();

          // Universal Navigate & Return Logic
          const draftStr = sessionStorage.getItem(INLINE_DRAFT_KEY);
          if (draftStr && newDeptId) {
            try {
              const draft = JSON.parse(draftStr);
              if (draft.targetField === "department_id") {
                draft.newValue = newDeptId;
                draft.newLabel = submissionData.name;
                sessionStorage.setItem(INLINE_DRAFT_KEY, JSON.stringify(draft));
                if (draft.sourcePath) {
                  navigate(draft.sourcePath);
                }
              }
            } catch (e) {
              console.error("Failed to parse inline draft", e);
            }
          }
        },
      });
    } else if (mode === "edit" && deptId) {
      updateMutation.mutate(
        { id: deptId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          // Prevent closing when clicking outside
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Department" : "Edit Department"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            {/* Department Name */}
            <InputField<DepartmentFormType>
              name="name"
              label="Department Name"
              placeholder="e.g. Computer Science"
              type="text"
              disabled={isPending}
              required
            />

            {/* Department Code */}
            <InputField<DepartmentFormType>
              name="code"
              label="Department Code"
              placeholder="e.g. CSE"
              type="text"
              disabled={isPending}
              required
            />

            <InputField<DepartmentFormType>
              name="established_year"
              label="Established Year"
              placeholder="e.g. 2010"
              type="number"
              disabled={isPending}
            />

            {/* Department Email */}
            <InputField<DepartmentFormType>
              name="email"
              label="Department Email"
              placeholder="dept@college.edu"
              type="email"
              disabled={isPending}
              required
            />

            {/* Phone Number */}
            <InputField<DepartmentFormType>
              name="phone"
              label="Phone Number"
              placeholder="e.g. +1-234-567-8901"
              type="tel"
              disabled={isPending}
            />

            {/* Status */}
            <SelectField<DepartmentFormType>
              name="status"
              label="Status"
              options={STATUS_OPTIONS}
              disabled={isPending}
              required
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Description - Full Width */}

            <TextareaField<DepartmentFormType>
              name="description"
              label="Description"
              placeholder="Brief description about the department"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Creating..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Department"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DepartmentDialog;
